import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import { handleError } from "../../utils/handleError";
import toast from "react-hot-toast";
import {
  createPromotionAPI,
  getAllPromotionsAPI,
  updatePromotionAPI,
} from "../../services/Promotions/promotion";
import { useDispatch } from "react-redux";
import { setListPromotions } from "../../redux/slice/promotions";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const PromotionModal = ({ open, setOpen, promotion, setLoading }) => {
  const [formData, setFormData] = useState({
    nameEstablishment: "",
    nameDiscount: "",
    discount: "",
    description: "",
    status: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (promotion) {
      setFormData({
        nameEstablishment: promotion?.nameEstablishment || "",
        discount: promotion?.discount || "",
        description: promotion?.description || "",
        status: promotion?.status === "active",
        nameDiscount: promotion?.nameDiscount || "",
      });
      if (promotion.urlImg) {
        setPreviewImage(promotion.urlImg);
      }
    } else {
      setFormData({
        nameEstablishment: "",
        nameDiscount: "",
        discount: "",
        description: "",
        status: false,
      });
      setPreviewImage("");
    }
    setImageFile(null);
  }, [promotion, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDiscountChange = (e) => {
    const { value } = e.target;
    // Eliminar todos los caracteres que no sean números o %
    const cleanedValue = value.replace(/[^0-9%]/g, "");

    // Si el valor no termina con %, y no está vacío, agregar %
    let formattedValue = cleanedValue;
    if (
      cleanedValue &&
      !cleanedValue.endsWith("%") &&
      !cleanedValue.includes("%")
    ) {
      formattedValue = `${cleanedValue}%`;
    }

    setFormData((prev) => ({
      ...prev,
      discount: formattedValue,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo (solo imágenes)
      if (!file.type.match("image.*")) {
        toast.error("Por favor, sube solo archivos de imagen");
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    // Validar que haya una imagen al crear (no requerida al actualizar)
    if (!promotion && !imageFile) {
      toast.error("Debes subir una imagen para crear la promoción");
      return false;
    }

    // Validar formato del descuento (debe ser un número seguido de %)
    const discountRegex = /^[0-9]+%$/;
    if (!discountRegex.test(formData.discount)) {
      toast.error(
        "El descuento debe ser un número seguido del símbolo % (ej: 25%)"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      // Validar el formulario antes de enviar
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      const data = new FormData();
      data.append("nameEstablishment", formData.nameEstablishment);
      data.append("nameDiscount", formData.nameDiscount);

      // Asegurarse de que el descuento tenga el formato correcto
      const discountValue = formData.discount.endsWith("%")
        ? formData.discount
        : `${formData.discount}%`;
      data.append("discount", discountValue);

      data.append("description", formData.description);
      data.append("status", formData.status ? "active" : "inactive");
      data.append("unloocked", false);

      if (imageFile) {
        data.append("urlImg", imageFile);
      } else if (promotion && previewImage) {
        // Si estamos actualizando y no hay nueva imagen, pero hay una imagen previa
        // (asumiendo que la API maneja esto adecuadamente)
      }

      promotion
        ? await updatePromotionAPI(data, promotion._id)
        : await createPromotionAPI(data);

      const responsePromotions = await getAllPromotionsAPI();
      dispatch(setListPromotions({ listPromotions: responsePromotions ?? [] }));
      toast.success(
        `Promoción ${promotion ? "actualizada" : "creada"} correctamente.`
      );
      setPreviewImage("");
      setImageFile(null);
      setFormData({
        nameEstablishment: "",
        nameDiscount: "",
        discount: "",
        description: "",
        status: false,
      });
      setOpen(false);
      setLoading(false);
    } catch (error) {
      const { message } = handleError(error);
      setLoading(false);
      toast.error(
        `No se pudo ${promotion ? "actualizar" : "crear"} la promoción. \n ${message}`
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {promotion ? "Actualizar Promoción" : "Crear Nueva Promoción"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box>
            <TextField
              fullWidth
              label="Nombre del Establecimiento"
              name="nameEstablishment"
              value={formData.nameEstablishment}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Nombre del Descuento"
              name="nameDiscount"
              value={formData.nameDiscount}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Descuento (ej: 25%)"
              name="discount"
              value={formData.discount}
              onChange={handleDiscountChange} // Usamos el manejador especial para descuento
              margin="normal"
              required
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*%?",
              }}
            />

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={4}
              required
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                />
              }
              label={formData?.status ? "Activa" : "Inactiva"}
              sx={{ mt: 1 }}
            />

            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography color="red">Solo puedes subir un QR.</Typography>
              <Button
                component="label"
                variant="outlined"
                color="#e7ef7d"
                sx={{ width: 300 }}
                startIcon={<CloudUploadIcon />}
              >
                Subir Imagen
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!promotion} // Hacer requerido solo para creación
                />
              </Button>

              {previewImage && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "150px" }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} color="whithe">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: "#e7ef7d" }}
          >
            {promotion ? "Actualizar Promoción" : "Crear Promoción"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PromotionModal;
